// DEV-ONLY store impersonation.
//
// Lets a developer view any shop's data by overriding the storeId the SPA talks
// to — without creating a per-shop account and signing in/out. You stay logged
// in as ONE account; only the store *scope* (the storeId sent as the REST
// `Authorization: Bearer <storeId>` header, plus the Firestore/RTDB
// `stores/<storeId>/...` paths) is redirected.
//
// EVERYTHING here is gated behind `import.meta.dev`. In a production build
// `import.meta.dev` is statically `false`, so the reads below constant-fold to
// no-ops and the whole feature is tree-shaken out of the bundle. It can never
// affect production.
//
// The override is applied once at boot, inside `useShopStore().init()`, on the
// storeId assigned right BEFORE `registerModule()` fans it out to axios +
// Firebase. Switching therefore requires a reload, which the dev panel does for
// you. See modules/core/components/dev/DevStorePanel.vue.

const DEV_STORE_KEY = "__dev_store_override";
const DEV_REAL_STORE_KEY = "__dev_real_store";

/** True only in `nuxt dev`. Every export below hard-returns when this is false. */
export const isDevStoreBypassAvailable = (): boolean => import.meta.dev;

/**
 * The currently active store override, or `null` when none is set (or in prod).
 * Trimmed; blank/whitespace is treated as unset.
 */
export function getDevStoreOverride(): string | null {
  if (!import.meta.dev) return null;
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(DEV_STORE_KEY);
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * สวมร้านอื่นอยู่ = ดูอย่างเดียว (dev เท่านั้น, prod เป็น `false` เสมอ)
 *
 * token ของเรามี custom claim เป็นร้านจริงของเรา RTDB จึงปฏิเสธการเขียน jobchain
 * ของร้านที่สวมอยู่ ซึ่งถูกแล้ว แต่ rtdbGuard จะตีความว่าเป็นเครื่องหน้าร้านที่ claim
 * พังแล้ว logout ทิ้ง จุดที่เช็คค่านี้:
 *   - rtdbGuard     : ไม่ alert / ไม่ logout แค่ warn
 *   - jobs.cloud    : ไม่ดัน job ขึ้น jobchain (เพิ่มแค่ในเครื่อง)
 *   - getMode()     : ใช้ได้แค่ unassigned / terminal_order โหมดอื่นคืน unassigned
 *                     งาน main cashier ที่เขียน cloud เองเบื้องหลังจึงไม่รัน (รับ jobRequest /
 *                     QR order ของร้านจริง, ดัน tables / live / storeStatus / tableMeta)
 *   - presence      : ไม่เขียน stores/{id}/online ไม่ให้เครื่อง dev โผล่ในร้านเขา
 */
export function isDevReadOnly(): boolean {
  if (!import.meta.dev) return false;
  return !!getDevStoreOverride();
}

/** Persist a new override. No-op in prod / SSR. Caller reloads to apply. */
export function setDevStoreOverride(storeId: string): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  const trimmed = storeId?.trim();
  try {
    if (trimmed) localStorage.setItem(DEV_STORE_KEY, trimmed);
    else localStorage.removeItem(DEV_STORE_KEY);
  } catch {}
}

/**
 * Remember the REAL storeId (the one the backend derived from your logged-in
 * uid) so the panel can show "which account you actually are" while
 * impersonating. Written once per boot from useShopStore().init().
 */
export function setDevRealStore(storeId: string): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  const trimmed = storeId?.trim();
  try {
    if (trimmed) localStorage.setItem(DEV_REAL_STORE_KEY, trimmed);
  } catch {}
}

/** The real logged-in storeId last seen at boot, or "". Dev-only. */
export function getDevRealStore(): string {
  if (!import.meta.dev) return "";
  if (typeof localStorage === "undefined") return "";
  try {
    return localStorage.getItem(DEV_REAL_STORE_KEY)?.trim() ?? "";
  } catch {
    return "";
  }
}

// The server's scope-bypass query pair (`key=value`) is a real backdoor usable
// straight from curl/Postman — so it must NEVER be hardcoded in source, or it
// would leak via the production source map even though the executable code is
// dead. Instead the dev stores it once in their OWN browser localStorage (this
// machine only); the code reads it at runtime and never contains the literal
// value. Set it in the DEV panel, or by hand in the console as a `<key>=<value>`
// string (do not commit the actual value anywhere).
//
// NOTE: this backend authorizes by `Authorization: Bearer <storeId>`, so simply
// overriding the storeId usually already grants full access — the bypass pair
// is only needed if/when the backend starts enforcing scope against your real
// uid. It is optional and appends nothing when left blank.
const DEV_BYPASS_KEY = "__dev_store_bypass";

/** The raw `key=value` bypass pair from localStorage, or null. Dev-only. */
export function getDevStoreBypass(): string | null {
  if (!import.meta.dev) return null;
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(DEV_BYPASS_KEY);
    const trimmed = raw?.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
}

/** Persist the bypass pair. No-op in prod / SSR. */
export function setDevStoreBypass(pair: string): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  const trimmed = pair?.trim();
  try {
    if (trimmed) localStorage.setItem(DEV_BYPASS_KEY, trimmed);
    else localStorage.removeItem(DEV_BYPASS_KEY);
  } catch {}
}

/**
 * DEV-ONLY query-string suffix that forces the server's scope bypass while a
 * store override is active. Append to any REST URL. Returns `&<key>=<value>`
 * from localStorage when impersonating AND a bypass pair is set, else `''`.
 * import.meta.dev-gated → empty (and tree-shaken) in prod, and the literal
 * secret is never present in source.
 */
export function devStoreBypassQuery(): string {
  if (!import.meta.dev) return "";
  if (!getDevStoreOverride()) return "";
  const bypass = getDevStoreBypass();
  return bypass ? `&${bypass}` : "";
}

/** Clear the override (back to your real logged-in shop). No-op in prod / SSR. */
export function clearDevStoreOverride(): void {
  if (!import.meta.dev) return;
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(DEV_STORE_KEY);
  } catch {}
}
